import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import {
  BILLING_ACCESS_DENIED_COPY,
  CREDIT_EMIT_PLAN,
  CREDIT_SOURCE_SYSTEM_LABELS,
  CREDIT_USAGE_CATEGORY_LABELS,
  canViewBilling,
  loadOrgBilling,
  type CreditLedgerEntry,
  type OrgBillingLoadResult,
  type OverageMode,
} from "@/lib/billing";

function formatUsd(amount: number | null): string {
  if (amount === null) return "Contact us";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCredits(n: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(n));
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function overageModeLabel(mode: OverageMode): string {
  if (mode === "auto_bill_blocks") return "Auto-bill overage blocks";
  if (mode === "hard_limit") return "Hard credit limit";
  return "Not set";
}

function Section({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 dark:border-white/10 dark:bg-[#1D232D]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function AuditEntryRow({ entry }: { entry: CreditLedgerEntry }) {
  const resources = entry.relatedResources || [];
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={entry.direction === "credit" ? "default" : "outline"}>
          {entry.direction === "credit" ? "Credit" : "Charge"}
        </Badge>
        <Badge variant="secondary">{CREDIT_USAGE_CATEGORY_LABELS[entry.category]}</Badge>
        <span
          className={
            entry.direction === "credit"
              ? "tabular-nums text-sm font-semibold text-emerald-700 dark:text-emerald-300"
              : "tabular-nums text-sm font-semibold text-gray-900 dark:text-white"
          }
        >
          {entry.direction === "credit" ? "+" : "−"}
          {formatCredits(entry.credits)} Credits
        </span>
        <span className="text-xs text-gray-400">{formatDateTime(entry.occurredAt)}</span>
      </div>
      <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{entry.summary}</p>
      <p className="mt-1 text-xs text-gray-500">
        {CREDIT_SOURCE_SYSTEM_LABELS[entry.sourceSystem]}
        {entry.quantity > 0 ? ` · qty ${formatCredits(entry.quantity)}` : ""}
        {entry.actorLabel ? ` · ${entry.actorLabel}` : ""}
        {entry.actorUserId ? ` · actor ${entry.actorUserId}` : ""}
      </p>
      {resources.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {resources.map((resource) => (
            <li key={`${resource.type}:${resource.id}`}>
              <Badge variant="outline" title={`${resource.type}:${resource.id}`}>
                {resource.label || `${resource.type} ${resource.id}`}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function BillingPage() {
  const state = useDirectoryState();
  const ctx = state.status === "ready" ? state.value : null;
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "audit" ? "audit" : "overview";
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<OrgBillingLoadResult | null>(null);

  const allowed = useMemo(
    () =>
      ctx
        ? canViewBilling({
            isSuperAdmin: ctx.isSuperAdmin,
            billingAccessGranted: ctx.me.billingAccessGranted,
          })
        : false,
    [ctx],
  );

  const load = useCallback(async () => {
    if (!ctx || !allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await loadOrgBilling({
        orgId: ctx.org?.orgId,
        orgLabel: ctx.org?.companyName || ctx.org?.domain || "your organization",
      });
      setResult(next);
      if (next.source === "error") {
        toast.error(next.error || "Could not load billing for this organization.");
      }
    } catch {
      toast.error("Could not load billing for this organization.");
      setResult({
        summary: null,
        source: "error",
        error: "Could not load billing for this organization.",
      });
    } finally {
      setLoading(false);
    }
  }, [allowed, ctx]);

  useEffect(() => {
    if (ctx) void load();
  }, [ctx, load]);

  if (!ctx) return null;

  if (!allowed) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing</h1>
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          {BILLING_ACCESS_DENIED_COPY}
        </p>
      </div>
    );
  }

  const summary = result?.summary;
  const loadError = result?.source === "error" ? result.error : undefined;
  const usedPct =
    summary && summary.balance.includedPool > 0
      ? Math.min(
          100,
          Math.round((summary.balance.usedCredits / summary.balance.includedPool) * 100),
        )
      : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {tab === "audit" ? "Credits audit" : "Billing"}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {tab === "audit"
              ? `Every Credits charge and credit for ${ctx.org?.companyName || ctx.org?.domain || "your organization"}, with enough detail to explain what happened.`
              : `Plan, Credits pool, usage, invoices, and the frozen rate card for ${ctx.org?.companyName || ctx.org?.domain || "your organization"}. Same ledger is also visible in Platform. Person pages stay triggers only.`}
          </p>
        </div>
        {result?.source === "live" ? <Badge variant="default">Live ledger</Badge> : null}
      </div>

      {loadError ? (
        <div className="space-y-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-100">
          <p>{loadError}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
        </div>
      ) : !summary ? (
        loadError ? null : (
          <p className="py-8 text-center text-sm text-gray-500">No billing data yet.</p>
        )
      ) : tab === "audit" ? (
        <Section
          title="Ledger activity"
          description="Auditable list of Credits debits and credits. Each row includes category, quantity, Credits, source system, time, actor, and related resource ids."
          action={
            <Link
              to="/billing"
              className="text-sm font-medium text-sky-700 underline dark:text-sky-300"
            >
              Back to overview
            </Link>
          }
        >
          {summary.auditEntries.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No ledger activity yet.</p>
          ) : (
            <ul className="-mx-5 divide-y divide-gray-100 sm:-mx-6 dark:divide-white/10">
              {summary.auditEntries.map((entry) => (
                <AuditEntryRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </Section>
      ) : (
        <>
          <Section
            title="Plan and hosting tier"
            description="One hosting tier and one unified Credits pool for customer-facing systems. Pay and Hub stay in the base tier. Ledger surfaces: Directory and Platform."
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled
                title="Upgrade flow coming later"
              >
                Upgrade (soon)
              </Button>
            }
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Current plan
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                  {summary.plan.planName}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatUsd(summary.plan.listPriceUsd)}
                  {summary.plan.listPriceUsd !== null ? " / month list" : ""}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Billing period
                </p>
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(summary.plan.periodStart)} to {formatDate(summary.plan.periodEnd)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Credits reset {formatDate(summary.plan.creditsResetAt)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Rate card</p>
                <p className="mt-1 font-mono text-sm font-medium text-gray-900 dark:text-white">
                  {summary.plan.rateCardVersion}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Locked {formatDate(summary.plan.rateCardLockedAt)}
                </p>
              </div>
            </div>
          </Section>

          <Section
            title="Credits balance"
            description="Included pool for this period. When Credits run out, choose auto-bill overage blocks or a hard limit below."
          >
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs text-gray-500">Included pool</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCredits(summary.balance.includedPool)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs text-gray-500">Used</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCredits(summary.balance.usedCredits)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs text-gray-500">Remaining</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCredits(summary.balance.remainingCredits)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs text-gray-500">Top-ups</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCredits(summary.balance.topUpCredits)}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <div className="mb-1 flex justify-between text-xs text-gray-500">
                <span>Period usage</span>
                <span>{usedPct}% of included pool</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-sky-500 transition-[width]"
                  style={{ width: `${usedPct}%` }}
                />
              </div>
            </div>
          </Section>

          <Section
            title="Usage by category"
            description="Credits spent across systems this period. Open Audit for the full explainable activity list."
            action={
              <Link
                to="/billing?tab=audit"
                className="text-sm font-medium text-sky-700 underline dark:text-sky-300"
              >
                Open audit
              </Link>
            }
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10">
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium">Quantity</th>
                    <th className="py-2 pr-4 font-medium">Credits</th>
                    <th className="py-2 font-medium">Sources</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                  {summary.usageByCategory
                    .filter((row) => row.credits > 0 || row.quantity > 0)
                    .map((row) => (
                      <tr key={row.category}>
                        <td className="py-2.5 pr-4 text-gray-900 dark:text-gray-100">
                          {CREDIT_USAGE_CATEGORY_LABELS[row.category]}
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums text-gray-700 dark:text-gray-300">
                          {formatCredits(row.quantity)}
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums font-medium text-gray-900 dark:text-white">
                          {formatCredits(row.credits)}
                        </td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-400">
                          {row.sourceSystems
                            .map((s) => CREDIT_SOURCE_SYSTEM_LABELS[s])
                            .join(", ") || "-"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Recent ledger activity"
            description="Latest auditable Credits movements. Every charge needs category, quantity, Credits, source, time, actor, and related ids."
            action={
              <Link
                to="/billing?tab=audit"
                className="text-sm font-medium text-sky-700 underline dark:text-sky-300"
              >
                Full audit
              </Link>
            }
          >
            {summary.auditEntries.length === 0 ? (
              <p className="text-sm text-gray-500">No ledger activity yet.</p>
            ) : (
              <ul className="-mx-5 divide-y divide-gray-100 sm:-mx-6 dark:divide-white/10">
                {summary.auditEntries.slice(0, 6).map((entry) => (
                  <AuditEntryRow key={entry.id} entry={entry} />
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Overage and payment"
            description="When Credits run out: auto-bill overage at $25 per block, or set a credit limit. Payment method is a Stripe placeholder only; no live keys in this app."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Overage rule
                </p>
                <p className="mt-1 text-base font-semibold text-gray-900 dark:text-white">
                  {overageModeLabel(summary.overage.mode)}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Blocks: {formatUsd(summary.overage.blockPriceUsd)} per{" "}
                  {formatCredits(summary.overage.blockSizeCredits)} Credits
                  {summary.overage.creditLimit !== null
                    ? ` · Limit ${formatCredits(summary.overage.creditLimit)}`
                    : ""}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" disabled>
                    Use auto-bill blocks
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled>
                    Set credit limit
                  </Button>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Payment method
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <span className="flex size-10 items-center justify-center rounded-md bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300">
                    <CreditCard className="size-5" aria-hidden />
                  </span>
                  <div>
                    {summary.overage.paymentMethod.status === "on_file" &&
                    summary.overage.paymentMethod.last4 ? (
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {(summary.overage.paymentMethod.brand || "Card").toUpperCase()} ····{" "}
                        {summary.overage.paymentMethod.last4}
                      </p>
                    ) : (
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        No card on file
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Stripe Customer Portal wiring lands with the live billing Worker.
                    </p>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-3" disabled>
                  Manage payment (soon)
                </Button>
              </div>
            </div>
          </Section>

          <Section
            title="Invoices and receipts"
            description="Hosting invoices for this organization. Receipt links appear when the billing API provides them."
          >
            {summary.invoices.length === 0 ? (
              <p className="text-sm text-gray-500">No invoices yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-white/10">
                {summary.invoices.map((invoice) => (
                  <li
                    key={invoice.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-3"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{invoice.number}</p>
                      <p className="text-xs text-gray-500">{formatDate(invoice.issuedAt)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="tabular-nums text-sm text-gray-800 dark:text-gray-200">
                        {formatUsd(invoice.amountUsd)}
                      </span>
                      <Badge variant="secondary">{invoice.status}</Badge>
                      {invoice.receiptUrl ? (
                        <a
                          href={invoice.receiptUrl}
                          className="text-sm font-medium text-sky-700 underline dark:text-sky-300"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Receipt
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">Receipt pending API</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Frozen rate card"
            description={`Version ${summary.rateCard.version} locked at signup for this org. Vendor cost changes adjust Credits weights on new versions; live customer dollar plans stay put.`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10">
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium">Unit</th>
                    <th className="py-2 font-medium">Credits / unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                  {summary.rateCard.lines.map((line) => (
                    <tr key={line.category}>
                      <td className="py-2.5 pr-4 text-gray-900 dark:text-gray-100">
                        {CREDIT_USAGE_CATEGORY_LABELS[line.category]}
                        {line.notes ? (
                          <span className="mt-0.5 block text-xs text-gray-500">{line.notes}</span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">
                        {line.unitLabel}
                      </td>
                      <td className="py-2.5 tabular-nums font-medium text-gray-900 dark:text-white">
                        {line.creditsPerUnit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Inbound emit plan"
            description="How each ATW system will push auditable usage into this shared ledger (Directory + Platform). Collection work lives mostly outside this repo."
          >
            <ul className="space-y-3">
              {CREDIT_EMIT_PLAN.map((hook) => (
                <li
                  key={`${hook.sourceSystem}-${hook.product}`}
                  className="rounded-lg border border-gray-200 px-4 py-3 dark:border-white/10"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-gray-900 dark:text-white">{hook.product}</p>
                    <Badge variant="outline">
                      {CREDIT_SOURCE_SYSTEM_LABELS[hook.sourceSystem]}
                    </Badge>
                    <Badge variant="secondary">{hook.status.replaceAll("_", " ")}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{hook.trigger}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Owner: {hook.emitOwner}. Categories:{" "}
                    {hook.categories.map((c) => CREDIT_USAGE_CATEGORY_LABELS[c]).join(", ")}.
                  </p>
                </li>
              ))}
            </ul>
          </Section>
        </>
      )}
    </div>
  );
}
